/**
 * This file is maintained by Stencil, do not directly modify this file
 */
'use client';

import React, {useEffect, useRef} from 'react';
import {useCommonData} from '@udemy-v2/common-data';
import {createAmplitudeTrackingCallback} from '@udemy-v2/amplitude';
import {useTracker} from '@udemy-v2/event-tracking';
import {initializeVendors} from '@/lib/vendors';
import {useI18n} from '@udemy-v2/i18n';
import {GoogleTagManager} from '@udemy-v2/gtag';

/**
 * Props for the Vendors component
 */
export interface VendorsProps {
    children?: React.ReactNode;
}

/**
 * Vendors component that initializes third-party services and renders vendor components
 * Uses common data from context to set up vendors like Sentry
 * Renders vendor components like GoogleTagManager
 *
 * @component
 * @param {Readonly<VendorsProps>} props - Component props with readonly constraint
 * @returns {JSX.Element} Vendor components and children wrapped with vendor initialization
 */
export function Vendors({children}: Readonly<VendorsProps>) {
    // Get common data from the CommonProviders context
    const commonData = useCommonData();
    const {locale} = useI18n();
    const tracker = useTracker();

    // Register the Amplitude tracking callback once on first render.
    // createAmplitudeTrackingCallback() automatically registers all default event
    // mappings, so this is the only integration point needed for Amplitude tracking.
    // Runs synchronously so the callback is in place before any child publishes an event.
    const amplitudeInitialized = useRef<boolean>(null);
    if (amplitudeInitialized.current == null) {
        // Cast bridges the structural gap between TrackingCallback (from @udemy-v2/amplitude)
        // and EventTrackingCallback (from @udemy-v2/event-tracking): both have identical
        // runtime shapes, but the event parameter types are defined independently in each
        // package to avoid a circular dependency.
        tracker.registerCallback(
            createAmplitudeTrackingCallback() as Parameters<typeof tracker.registerCallback>[0],
        );
        amplitudeInitialized.current = true;
    }

    // Track if vendors have been initialized to prevent multiple calls
    const vendorsInitialized = useRef(false);

    useEffect(() => {
        // Initialize vendors only once when common data is available and fully loaded
        if (commonData.page?.isLoading === false && !vendorsInitialized.current) {
            try {
                initializeVendors(commonData, locale);
                vendorsInitialized.current = true;
            } catch (error) {
                // Log error but don't break the component
                console.error('Failed to initialize vendors:', error);
            }
        }
    }, [commonData, locale]);

    // Render vendor components and children
    return (
        <>
            <GoogleTagManager />
            {children}
        </>
    );
}
